import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BannerFichas from '../../components/BannerFichas/BannerFichas';
import Navbar from '../../components/Navbar/Navbar';
import SessionBar from '../../components/SessionBar/SessionBar';
import Footer from '../../components/Footer/Footer';
import BotonChico03 from '../../components/Botones/BotonChico03/BotonChico03';
import { fetchUserData } from '../../utils/api';
import axiosInstance from '../../services/AxiosInstance';
import { ROUTES } from '../../const/routes';
import { useUser } from '../../context/UserContext';
import {
  AVATAR_POR_DEFECTO,
  getAvatarSrc,
  handleAvatarError,
} from '../../utils/avatar';

const formatFecha = (fecha) => {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrency = (value) =>
  value.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const AdminUsuarioDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { userData: currentUser, silentRefreshUserData } = useUser();
  const [sessionUser, setSessionUser] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(false);
  const [formData, setFormData] = useState({});
  const [mensaje, setMensaje] = useState(null);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [payments, setPayments] = useState([]);
  const [cargandoPayments, setCargandoPayments] = useState(false);
  const [paymentPage, setPaymentPage] = useState(1);
  const PAYMENTS_PER_PAGE = 5;

  // Historial de jugadas
  const [jugadas, setJugadas] = useState([]);
  const [cargandoJugadas, setCargandoJugadas] = useState(false);
  const [jugadaPage, setJugadaPage] = useState(1);
  const [jugadaTotalPages, setJugadaTotalPages] = useState(1);
  const [jugadaFiltro, setJugadaFiltro] = useState('All');
  const JUGADAS_PER_PAGE = 10;

  useEffect(() => {
    fetchUserData(navigate, setSessionUser);
  }, [navigate]);

  const fetchPayments = useCallback(async () => {
    setCargandoPayments(true);
    try {
      const response = await axiosInstance.get(
        `/api/admin/usuario/${id}/payments`
      );
      setPayments(response.data);
    } catch (err) {
      // Silencioso - la sección simplemente mostrará vacío
    } finally {
      setCargandoPayments(false);
    }
  }, [id]);

  const fetchJugadas = useCallback(async () => {
    setCargandoJugadas(true);
    try {
      const response = await axiosInstance.get(
        `/api/admin/usuario/${id}/jugadas`,
        {
          params: {
            page: jugadaPage,
            limit: JUGADAS_PER_PAGE,
            filtro: jugadaFiltro,
          },
        }
      );
      setJugadas(response.data.data || []);
      setJugadaTotalPages(response.data.totalPages || 1);
    } catch (err) {
      // Silencioso
    } finally {
      setCargandoJugadas(false);
    }
  }, [id, jugadaPage, jugadaFiltro]);

  const fetchUsuario = useCallback(async () => {
    setCargando(true);
    try {
      const response = await axiosInstance.get(`/api/admin/usuario/${id}`);
      setUsuario(response.data);
      setFormData({
        nick_usuario: response.data.nick_usuario || '',
        nombre_usuario: response.data.nombre_usuario || '',
        apellido_usuario: response.data.apellido_usuario || '',
        email: response.data.email || '',
        avatar: response.data.avatar || AVATAR_POR_DEFECTO,
        id_rol: response.data.id_rol || 1,
        saldo: String(response.data.saldo ?? 0),
        email_verificado: response.data.email_verificado ?? false,
      });
    } catch (err) {
      console.error('Error al obtener usuario:', err);
      setError(t('adminUserDetail.loadError'));
    } finally {
      setCargando(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchUsuario();
    fetchPayments();
  }, [fetchUsuario, fetchPayments]);

  useEffect(() => {
    fetchJugadas();
  }, [fetchJugadas]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'id_rol' ? Number(value) :
        name === 'email_verificado' ? value === 'true' :
        value,
    }));
  };

  const handleGuardar = async () => {
    setMensaje(null);
    setError(null);
    try {
      const SALDO_MAXIMO = 99999999;
      const saldoNumerico = Math.min(
        Math.max(0, Number(formData.saldo) || 0),
        SALDO_MAXIMO
      );
      if (Number(formData.saldo) > SALDO_MAXIMO) {
        setError(
          t('adminUserDetail.maxBalanceError', {
            amount: SALDO_MAXIMO.toLocaleString('es-AR'),
          })
        );
        return;
      }
      const response = await axiosInstance.patch(`/api/admin/usuario/${id}`, {
        ...formData,
        saldo: saldoNumerico,
      });
      setUsuario(response.data);
      setEditando(false);
      setMensaje(t('adminUserDetail.updateSuccess'));
      if (currentUser && currentUser.id === parseInt(id)) {
        silentRefreshUserData();
      }
      setTimeout(() => setMensaje(null), 3000);
    } catch (err) {
      console.error('Error al actualizar:', err);
      setError(err.response?.data?.msg || t('adminUserDetail.updateError'));
      setTimeout(() => setError(null), 4000);
    }
  };

  const handleToggleEstado = async () => {
    setToggling(true);
    setMensaje(null);
    setError(null);
    try {
      const response = await axiosInstance.patch(
        `/api/admin/usuario/${id}/toggle-estado`
      );
      setUsuario(response.data);
      setMensaje(
        response.data.deshabilitado
          ? t('adminUserDetail.disableSuccess')
          : t('adminUserDetail.enableSuccess')
      );
      setTimeout(() => setMensaje(null), 3000);
    } catch (err) {
      console.error('Error al cambiar estado:', err);
      setError(t('adminUserDetail.toggleError'));
      setTimeout(() => setError(null), 4000);
    } finally {
      setToggling(false);
    }
  };

  const handleCancelar = () => {
    setEditando(false);
    setError(null);
    if (usuario) {
      setFormData({
        nick_usuario: usuario.nick_usuario || '',
        nombre_usuario: usuario.nombre_usuario || '',
        apellido_usuario: usuario.apellido_usuario || '',
        email: usuario.email || '',
        avatar: usuario.avatar || AVATAR_POR_DEFECTO,
        id_rol: usuario.id_rol || 1,
        saldo: String(usuario.saldo ?? 0),
        email_verificado: usuario.email_verificado ?? false,
      });
    }
  };

  const estaDeshabilitado = !!usuario?.deshabilitado;
  const avatarSrc = getAvatarSrc(usuario?.avatar);
  const emailVerificado = !!usuario?.email_verificado;

  return (
    <div className="flex flex-col min-h-screen">
      <BannerFichas />
      <div className="bg-gradient-to-b from-slate-950 to-violet-950 flex-grow">
        <Navbar />
        {sessionUser && <SessionBar />}
        <div className="flex flex-col items-center py-6 md:py-10 px-2 lg:px-10">
          {/* Botón volver */}
          <div className="w-full max-w-5xl mb-4">
            <button
              onClick={() => navigate(ROUTES.admin)}
              className="text-sm text-purple-400 hover:text-purple-300 transition-colors duration-200 flex items-center gap-1"
            >
              ← {t('adminUserDetail.backButton')}
            </button>
          </div>

          <h2 className="text-lg md:text-xl lg:text-2xl text-purple-400 font-bold mb-3 md:mb-4">
            {t('adminUserDetail.title')}
          </h2>

          {/* Mensajes */}
          {mensaje && (
            <div className="w-full max-w-5xl mb-4 bg-green-500/20 border border-green-500/40 text-green-400 text-sm px-4 py-3 rounded-lg text-center">
              {mensaje}
            </div>
          )}
          {error && (
            <div className="w-full max-w-5xl mb-4 bg-red-500/20 border border-red-500/40 text-red-400 text-sm px-4 py-3 rounded-lg text-center">
              {error}
            </div>
          )}

          <div className="w-full max-w-5xl">
            <div
              className={`bg-bgContenedorOscuro rounded-lg px-4 md:px-8 py-6
                border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro
                shadow-[0_0_20px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_30px_rgba(100,116,139,1)]`}
            >
              {cargando ? (
                <div className="text-center text-gray-300 py-10 animate-pulse">
                  {t('adminUserDetail.loading')}
                </div>
              ) : !usuario ? (
                <div className="text-center text-red-400 py-10">
                  {t('adminUserDetail.notFound')}
                </div>
              ) : (
                <>
                  {/* Cabecera con avatar */}
                  <div className="flex flex-col sm:flex-row items-center gap-4 mb-6 pb-6 border-b-2 border-slate-700">
                    <img
                      src={avatarSrc}
                      alt={`Avatar de ${usuario.nick_usuario}`}
                      onError={handleAvatarError}
                      className={`w-20 h-20 rounded-full border-4 bg-gray-600 object-cover ${
                        estaDeshabilitado
                          ? 'border-red-500/60'
                          : 'border-purple-500/60'
                      }`}
                    />
                    <div className="text-center sm:text-left">
                      <h3 className="text-xl font-bold text-white">
                        {usuario.nick_usuario}
                      </h3>
                      <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-semibold ${
                            usuario.id_rol === 2
                              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                              : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                          }`}
                        >
                          {usuario.rol?.nombre_rol || t('adminUserDetail.noRole')}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-semibold ${
                            estaDeshabilitado
                              ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                              : 'bg-green-500/20 text-green-400 border border-green-500/40'
                          }`}
                        >
                          {estaDeshabilitado
                            ? t('adminUsers.disabled')
                            : t('adminUsers.enabled')}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-semibold ${
                            emailVerificado
                              ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                              : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                          }`}
                        >
                          {emailVerificado
                            ? t('adminUserDetail.emailVerifiedBadge')
                            : t('adminUserDetail.emailNotVerifiedBadge')}
                        </span>
                      </div>
                      {estaDeshabilitado && (
                        <p className="text-xs text-red-400 mt-3">
                          {t('adminUserDetail.disabledDate')}{formatFecha(usuario.deshabilitado)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Datos del usuario */}
                  <div className="space-y-5 md:space-y-6">
                    <Campo label={t('adminUserDetail.idField')} value={usuario.id} editable={false} />
                    <Campo
                      label={t('adminUserDetail.nickField')}
                      name="nick_usuario"
                      value={
                        editando ? formData.nick_usuario : usuario.nick_usuario
                      }
                      editable={editando}
                      onChange={handleChange}
                      maxLength={15}
                    />
                    <Campo
                      label={t('adminUserDetail.nameField')}
                      name="nombre_usuario"
                      value={
                        editando
                          ? formData.nombre_usuario
                          : usuario.nombre_usuario || '-'
                      }
                      editable={editando}
                      onChange={handleChange}
                      maxLength={20}
                    />
                    <Campo
                      label={t('adminUserDetail.lastNameField')}
                      name="apellido_usuario"
                      value={
                        editando
                          ? formData.apellido_usuario
                          : usuario.apellido_usuario || '-'
                      }
                      editable={editando}
                      onChange={handleChange}
                      maxLength={20}
                    />
                    <Campo
                      label={t('adminUserDetail.emailField')}
                      name="email"
                      value={editando ? formData.email : usuario.email || '-'}
                      editable={editando}
                      onChange={handleChange}
                      type="email"
                    />
                    <Campo
                      label={t('adminUserDetail.balanceField')}
                      name="saldo"
                      value={
                        editando
                          ? formData.saldo
                          : `$ ${formatCurrency(usuario.saldo)}`
                      }
                      editable={editando}
                      onChange={handleChange}
                      type="saldo"
                    />
                    <Campo
                      label={t('adminUserDetail.roleField')}
                      name="id_rol"
                      value={
                        editando
                          ? formData.id_rol
                          : usuario.rol?.nombre_rol || '-'
                      }
                      editable={editando}
                      onChange={handleChange}
                      type="select"
                      options={[
                        { value: 1, label: t('adminUserDetail.roleJugador') },
                        { value: 2, label: t('adminUserDetail.roleAdmin') },
                      ]}
                    />
                    <Campo
                      label={t('adminUserDetail.roleIdField')}
                      value={usuario.id_rol ?? '-'}
                      editable={false}
                    />
                    <Campo
                      label={t('adminUserDetail.avatarField')}
                      name="avatar"
                      value={
                        editando
                          ? formData.avatar
                          : usuario.avatar || AVATAR_POR_DEFECTO
                      }
                      editable={editando}
                      onChange={handleChange}
                      maxLength={20}
                    />
                    <Campo
                      label={t('adminUserDetail.emailVerifiedField')}
                      name="email_verificado"
                      value={editando ? String(formData.email_verificado) : (
                        <span
                          className={`inline-flex w-fit items-center rounded-full border px-2 py-1 text-xs font-semibold ${
                            emailVerificado
                              ? 'border-cyan-500/40 bg-cyan-500/20 text-cyan-300'
                              : 'border-amber-500/40 bg-amber-500/20 text-amber-300'
                          }`}
                        >
                          {emailVerificado ? t('adminUserDetail.yes') : t('adminUserDetail.no')}
                        </span>
                      )}
                      editable={editando}
                      onChange={handleChange}
                      type="select"
                      options={[
                        { value: 'true', label: t('adminUserDetail.yes') },
                        { value: 'false', label: t('adminUserDetail.no') },
                      ]}
                    />
                    <Campo
                      label={t('adminUserDetail.accountStatusField')}
                      value={
                        <span
                          className={`inline-flex w-fit items-center rounded-full border px-2 py-1 text-xs font-semibold ${
                            estaDeshabilitado
                              ? 'border-red-500/40 bg-red-500/20 text-red-400'
                              : 'border-green-500/40 bg-green-500/20 text-green-400'
                          }`}
                        >
                          {estaDeshabilitado
                            ? t('adminUserDetail.disabledStatus')
                            : t('adminUserDetail.enabledStatus')}
                        </span>
                      }
                      editable={false}
                    />
                    <Campo
                      label={t('adminUserDetail.disabledDateField')}
                      value={formatFecha(usuario.deshabilitado)}
                      editable={false}
                    />
                    <Campo
                      label={t('adminUserDetail.birthdateField')}
                      value={
                        usuario.fecha_nacimiento
                          ? new Date(
                              usuario.fecha_nacimiento
                            ).toLocaleDateString('es-AR', { timeZone: 'UTC' })
                          : '-'
                      }
                      editable={false}
                    />
                    <Campo
                      label={t('adminUserDetail.descriptionField')}
                      value={usuario.descripcion || '-'}
                      editable={false}
                    />
                    <Campo
                      label={t('adminUserDetail.resetsField')}
                      value={usuario.reset ?? 0}
                      editable={false}
                    />
                    <Campo
                      label={t('adminUserDetail.createdDateField')}
                      value={formatFecha(usuario.createdAt)}
                      editable={false}
                    />
                  </div>

                  {/* Botones de acción */}
                  <div className="flex flex-wrap justify-center gap-3 mt-8">
                    {editando ? (
                      <>
                        <BotonChico03
                          texto={t('adminUserDetail.saveButton')}
                          bgColor="green"
                          funcion={handleGuardar}
                        />
                        <BotonChico03
                          texto={t('adminUserDetail.cancelButton')}
                          bgColor="gray"
                          funcion={handleCancelar}
                        />
                      </>
                    ) : (
                      <>
                        <BotonChico03
                          texto={t('adminUserDetail.editButton')}
                          bgColor="blue"
                          funcion={() => setEditando(true)}
                        />
                        <BotonChico03
                          texto={
                            estaDeshabilitado
                              ? t('adminUserDetail.enableButton')
                              : t('adminUserDetail.disableButton')
                          }
                          bgColor={estaDeshabilitado ? 'green' : 'red'}
                          funcion={handleToggleEstado}
                        />
                      </>
                    )}
                  </div>

                  {/* ── Sección de Pagos ── */}
                  <div className="mt-8 pt-6 border-t-2 border-slate-700">
                    <h3 className="text-lg font-bold text-purple-400 mb-4">
                      {t('adminUserDetail.paymentsSection')}
                    </h3>
                    {cargandoPayments ? (
                      <p className="text-gray-400 text-sm animate-pulse">
                        {t('adminUserDetail.loadingPayments')}
                      </p>
                    ) : payments.length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        {t('adminUserDetail.noPayments')}
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-[800px] w-full text-sm text-left">
                          <thead>
                            <tr className="border-b border-slate-600 text-purple-300">
                              <th className="py-2 px-2">{t('adminUserDetail.paymentId')}</th>
                              <th className="py-2 px-2">{t('adminUserDetail.paymentDate')}</th>
                              <th className="py-2 px-2">{t('adminUserDetail.paymentAmount')}</th>
                              <th className="py-2 px-2">{t('adminUserDetail.paymentStatus')}</th>
                              <th className="py-2 px-2">{t('adminUserDetail.paymentMethod')}</th>
                              <th className="py-2 px-2">{t('adminUserDetail.paymentDescription')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments
                              .slice(
                                (paymentPage - 1) * PAYMENTS_PER_PAGE,
                                paymentPage * PAYMENTS_PER_PAGE
                              )
                              .map((p) => (
                                <tr
                                  key={p.id}
                                  className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                                >
                                  <td className="py-2 px-2 text-gray-300">
                                    #{p.id}
                                  </td>
                                  <td className="py-2 px-2 text-gray-300 whitespace-nowrap">
                                    {formatFecha(p.createdAt)}
                                  </td>
                                  <td className="py-2 px-2 text-yellow-400 font-semibold whitespace-nowrap">
                                    ${' '}
                                    {p.monto != null
                                      ? formatCurrency(p.monto)
                                      : '-'}
                                  </td>
                                  <td className="py-2 px-2">
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                        p.estado === 'approved'
                                          ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                                          : p.estado === 'rejected'
                                            ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                                            : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                                      }`}
                                    >
                                      {p.estado === 'approved'
                                        ? t('adminUserDetail.approved')
                                        : p.estado === 'rejected'
                                          ? t('adminUserDetail.rejected')
                                          : p.estado === 'pending'
                                            ? t('adminUserDetail.pending')
                                            : p.estado || '-'}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2 text-gray-300">
                                    {p.medio_pago || '-'}
                                  </td>
                                  <td className="py-2 px-2 text-gray-400 whitespace-nowrap">
                                    {p.descripcion || '-'}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>

                        {/* Paginado */}
                        {payments.length > PAYMENTS_PER_PAGE && (
                          <div className="flex items-center justify-between mt-4 mb-3 pt-3 border-t border-slate-700">
                            <span className="text-xs text-gray-400">
                              {t('adminUserDetail.showing')}{' '}
                              {(paymentPage - 1) * PAYMENTS_PER_PAGE + 1}-
                              {Math.min(
                                paymentPage * PAYMENTS_PER_PAGE,
                                payments.length
                              )}{' '}
                              {t('adminUserDetail.of')} {payments.length} {t('adminUserDetail.payments')}
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  setPaymentPage((p) => Math.max(1, p - 1))
                                }
                                disabled={paymentPage === 1}
                                className="px-3 py-1 text-xs rounded-md bg-slate-700 text-gray-300 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                {t('adminUserDetail.previousPayment')}
                              </button>
                              <span className="px-3 py-1 text-xs text-purple-300 font-semibold">
                                {paymentPage} /{' '}
                                {Math.ceil(payments.length / PAYMENTS_PER_PAGE)}
                              </span>
                              <button
                                onClick={() =>
                                  setPaymentPage((p) =>
                                    Math.min(
                                      Math.ceil(
                                        payments.length / PAYMENTS_PER_PAGE
                                      ),
                                      p + 1
                                    )
                                  )
                                }
                                disabled={
                                  paymentPage >=
                                  Math.ceil(payments.length / PAYMENTS_PER_PAGE)
                                }
                                className="px-3 py-1 text-xs rounded-md bg-slate-700 text-gray-300 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                {t('adminUserDetail.nextPayment')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Sección de Historial de Jugadas ── */}
                  <div className="mt-8 pt-6 border-t-2 border-slate-700">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <h3 className="text-lg font-bold text-purple-400">
                        {t('adminUserDetail.gamesHistorySection')}
                      </h3>
                      <div className="flex items-center gap-2">
                        <label className="text-gray-400 text-xs">
                          {t('adminUserDetail.filterLabel')}
                        </label>
                        <select
                          value={jugadaFiltro}
                          onChange={(e) => {
                            setJugadaFiltro(e.target.value);
                            setJugadaPage(1);
                          }}
                          className="bg-slate-700 border border-slate-600 text-white text-xs rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="All">{t('adminUserDetail.filterAll')}</option>
                          <option value="Roulette">{t('adminUserDetail.filterRoulette')}</option>
                          <option value="Slots">{t('adminUserDetail.filterSlots')}</option>
                          <option value="BlackJack">{t('adminUserDetail.filterBlackjack')}</option>
                        </select>
                      </div>
                    </div>
                    {cargandoJugadas ? (
                      <p className="text-gray-400 text-sm animate-pulse">
                        {t('adminUserDetail.loadingGames')}
                      </p>
                    ) : jugadas.length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        {t('adminUserDetail.noGames')}
                      </p>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-[700px] w-full text-sm text-left">
                            <thead>
                              <tr className="border-b border-slate-600 text-purple-300">
                                <th className="py-2 px-2">{t('adminUserDetail.paymentId')}</th>
                                <th className="py-2 px-2">{t('adminUserDetail.paymentDate')}</th>
                                <th className="py-2 px-2">{t('adminUserDetail.gameColumn')}</th>
                                <th className="py-2 px-2 text-right">{t('adminUserDetail.betColumn')}</th>
                                <th className="py-2 px-2 text-right">{t('adminUserDetail.wonColumn')}</th>
                                <th className="py-2 px-2 text-right">{t('adminUserDetail.resultColumn')}</th>
                                <th className="py-2 px-2 text-center">{t('adminUserDetail.actionColumn')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {jugadas.map((j) => {
                                const ganancia =
                                  j.total_recompensa - j.total_apostado;
                                const juegoColor =
                                  j.nombre_juego === 'Roulette'
                                    ? 'bg-green-500/20 text-green-400 border-green-500/40'
                                    : j.nombre_juego === 'Slots'
                                      ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40'
                                      : j.nombre_juego === 'BlackJack'
                                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/40'
                                        : 'bg-gray-500/20 text-gray-400 border-gray-500/40';
                                return (
                                  <tr
                                    key={j.id}
                                    className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                                  >
                                    <td className="py-2 px-2 text-gray-300">
                                      #{j.id}
                                    </td>
                                    <td className="py-2 px-2 text-gray-300 whitespace-nowrap">
                                      {formatFecha(j.hora_jugada)}
                                    </td>
                                    <td className="py-2 px-2">
                                      <span
                                        className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${juegoColor}`}
                                      >
                                        {t(`backend.gameName.${j.nombre_juego}`, j.nombre_juego)}
                                      </span>
                                      {j.en_proceso && (
                                        <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-orange-500/20 text-orange-400">
                                          {t('adminUserDetail.inProgress')}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-2 px-2 text-gray-300 text-right whitespace-nowrap">
                                      $ {formatCurrency(j.total_apostado)}
                                    </td>
                                    <td className="py-2 px-2 text-yellow-400 font-semibold text-right whitespace-nowrap">
                                      $ {formatCurrency(j.total_recompensa)}
                                    </td>
                                    <td
                                      className={`py-2 px-2 text-right font-semibold whitespace-nowrap ${
                                        ganancia > 0
                                          ? 'text-green-400'
                                          : ganancia < 0
                                            ? 'text-red-400'
                                            : 'text-gray-400'
                                      }`}
                                    >
                                      {ganancia > 0 ? '+' : ''}${' '}
                                      {formatCurrency(ganancia)}
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      <button
                                        onClick={() =>
                                          navigate(
                                            `${ROUTES.detallesJugada}/${j.id}`
                                          )
                                        }
                                        className="px-2 py-1 text-xs rounded-md bg-purple-600/80 text-white hover:bg-purple-500 transition-colors font-medium"
                                      >
                                        {t('adminUserDetail.detailButton')}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Paginado jugadas */}
                        <div className="flex items-center justify-between mt-4 mb-3 pt-3 border-t border-slate-700">
                          <span className="text-xs text-gray-400">
                            {t('adminUserDetail.pageLabel')} {jugadaPage} {t('adminUserDetail.of')} {jugadaTotalPages}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                setJugadaPage((p) => Math.max(1, p - 1))
                              }
                              disabled={jugadaPage === 1}
                              className="px-3 py-1 text-xs rounded-md bg-slate-700 text-gray-300 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              {t('adminUserDetail.previousPayment')}
                            </button>
                            <span className="px-3 py-1 text-xs text-purple-300 font-semibold">
                              {jugadaPage} / {jugadaTotalPages}
                            </span>
                            <button
                              onClick={() =>
                                setJugadaPage((p) =>
                                  Math.min(jugadaTotalPages, p + 1)
                                )
                              }
                              disabled={jugadaPage >= jugadaTotalPages}
                              className="px-3 py-1 text-xs rounded-md bg-slate-700 text-gray-300 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              {t('adminUserDetail.nextPayment')}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const Campo = ({
  label,
  name,
  value,
  editable,
  onChange,
  type = 'text',
  options,
  maxLength,
}) => {
  const handleSaldoChange = (e) => {
    const raw = e.target.value;
    // Permitir vacío, dígitos y un solo punto decimal, con tope de 99999999
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
      const num = Number(raw);
      if (raw !== '' && num > 99999999) return;
      onChange({ target: { name, value: raw } });
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8 md:gap-10 py-1">
      <span className="text-sm text-purple-400 font-semibold sm:w-48 md:w-56 flex-shrink-0">
        {label}
      </span>
      {editable ? (
        type === 'select' ? (
          <select
            name={name}
            value={value}
            onChange={onChange}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : type === 'saldo' ? (
          <input
            type="text"
            inputMode="decimal"
            name={name}
            value={value}
            onChange={handleSaldoChange}
            placeholder="0"
            className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        ) : (
          <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            maxLength={maxLength}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        )
      ) : (
        <span className="text-sm text-gray-300 flex-1">{value}</span>
      )}
    </div>
  );
};

export default AdminUsuarioDetalle;
